/**
 * Раздел «Голос клиента» (#848, программа #841): о чём говорят клиенты по
 * всему потоку, по адресатам решения — продажи, маркетинг, продукт и сервис,
 * сайт и контент.
 *
 * Один компонент для портала (`/analytics/voice`) и встроенного Bitrix24
 * (вкладка `voice`): состояние — вкладка, фильтр, раскрытая строка — живёт в
 * адресе оболочки, и «Назад» из разговора возвращает туда же.
 *
 * Pro и выше. Доступ в портале — `useFeatureGate` (`gates` из
 * `/feature-flags`, #1231): тариф ниже, выключено и «не удалось узнать» —
 * три разных экрана. Встроенный Bitrix24 карты флагов не держит и передаёт
 * `access` из `features` сессии. Решает всё равно сервер: если раздел
 * закрыт, вкладки с данными получают 403 с кодом и говорят об этом
 * словами. «Продажи» — экран
 * «Возражения», который открыт всем пакетам по своему входу из Quality, —
 * внутри раздела тоже закрыт вместе с разделом.
 *
 * Вопросы клиентов и причины отказа (#843, #844) — блоки «Продаж» и «Сайта и
 * контента» за своим флагом `enableCustomerQuestions` (Pro и выше, бета с PR
 * выпуска, спека 2026-10-02 §8 п. 9). Флаг читается из карты флагов: у
 * портала — вердикт `/feature-flags`, у встроенного Bitrix24 — список флагов
 * сессии (#1347, #1605). Сервер шлёт блоки по тому же флагу: показываются
 * они, только когда открыто и там, и здесь. Карта, которая не ответила
 * (сбой, degraded), — не «выключено»: тогда решает ответ сервера.
 *
 * «Продажи» и «Сайт и контент» — слоями (#2060, #2072: `VoiceSalesTab`,
 * `VoiceSiteQuestions`): сводка, разделы, топ-5.
 *
 * Темы разговоров (#845 PR 6) — раздел «Продаж» и «Сайта и контента», а без
 * вопросов клиентов — блок под пояснением (`VoiceSiteClosed`) за тем же флагом
 * `enableConversationThemes`, от вопросов клиентов не зависят.
 *
 * Набор тем организации (#845) — панель из шапки раздела, рядом с вкладками:
 * за шлюзом раздела и своим флагом `enableConversationThemes` (бета: включён
 * по тарифу с Pro, организация может выключить), в портале и во встроенном Bitrix24
 * одинаково — флаг из карты флагов сервера.
 *
 * Фильтр темы (#2229, спека 2026-10-11-voice-report-layers.md §2.5, §3) —
 * выбор в шапке рядом с «Темами», тема в адресе (`voiceTheme`) и сужает каждый
 * блок каждой вкладки. Отказ фильтра на любой вкладке (403 `themes_disabled`,
 * 400 `theme_filter_invalid`) снимает тему из адреса и говорит об этом здесь,
 * над вкладками: страница перечитывается без фильтра.
 */
import { useState } from 'react';
import { VOICE_OF_CUSTOMER_FLAG_KEY } from '@aica/core';
import { useFeatureGate } from '@/contexts/FeatureFlagsContext';
import { LockedDemoProvider } from '@/contexts/LockedDemoContext';
import { lockedDemoPeek } from '@/features/demo/locked-demo';
import { VoiceDemoAnswerProbe } from './VoiceDemoAnswerProbe';
import { VoiceDemoStatusProbe, type VoiceDemoStatus } from './VoiceDemoStatusProbe';
import type { VoiceSectionAccess } from './VoiceLocked';
import { VoiceOfCustomerSection } from './VoiceOfCustomerSection';
import type { VoiceSearch } from './voice-search';

export interface VoiceOfCustomerViewProps {
  search: VoiceSearch;
  /** Merge a change into the shell's address (replacing the history entry). */
  onSearchChange: (patch: Partial<VoiceSearch>) => void;
  /** Open a conversation of the drill-down — the shell knows how (page or tab). */
  onOpenCall: (recordId: string) => void;
  /**
   * Whether the section is open, from a shell without the flag map (the
   * Bitrix24 session's features). Absent — the portal: the view asks
   * `useFeatureGate` itself.
   */
  access?: VoiceSectionAccess;
  /** Ask the shell's access check again — goes with `access`. */
  onRetryAccess?: () => void;
  upgradeHref?: string;
}

/**
 * The section's gate, and the demo of a closed section (#2816): below Pro with
 * the organization's demo active the section opens on the demo — every tab asks
 * its route, which answers with demo rows alone (`demoOnly`) — and a banner
 * says so; a tab the route refuses (403) is the plan screen as before. Below
 * Pro without demo it is the plan screen at once.
 *
 * One tree whatever the access: the same provider over the same section at the
 * same place, so a change of access (a plan change, a retried check) never
 * remounts the section and drops what it holds — the open themes panel, the
 * theme filter's refusal. Only a plan below Pro asks the demo rule — and the
 * probe that does is a sibling, not a wrapper: every other state is decided
 * without it (no request for a screen that never draws a tab).
 *
 * The open tab's answer is believed too, as on the deals (`lockedDemoView`): a
 * plan lowered with the page open leaves the client's verdict `enabled` while
 * the server already answers the demo (`demoOnly`). The section is then drawn
 * as the demo — banner, closed FAQ draft and theme edits — though its requests
 * keep the keys of the verdict they were asked by.
 */
export function VoiceOfCustomerView(props: VoiceOfCustomerViewProps) {
  const { access: shellAccess, onRetryAccess } = props;
  const gate = useFeatureGate(VOICE_OF_CUSTOMER_FLAG_KEY, {
    refreshOnMount: shellAccess === undefined,
  });
  const access: VoiceSectionAccess = shellAccess ?? gate.state;
  const retryAccess = shellAccess === undefined ? gate.retry : onRetryAccess;
  const sessionShell = shellAccess !== undefined;
  const belowPlan = access === 'tier_not_eligible';
  const [demo, setDemo] = useState<VoiceDemoStatus | undefined>(undefined);
  // Below the plan and the status not reported yet: it is not known, so neither
  // the plan screen nor a request yet.
  const peek = lockedDemoPeek(belowPlan, demo ?? { active: false, loading: true });
  // The section opens on the demo only if its requests ask for it: a link or a
  // window that names `includeDemo=no` (`useStreamDemoFilter`, read by the
  // probes) makes every read 403 below the plan — then it is the plan screen,
  // not a section of refusals.
  const [openDeclinesDemo, setOpenDeclinesDemo] = useState(false);
  const declinesDemo = belowPlan ? demo?.declinesDemo === true : openDeclinesDemo;
  const askedDemo = peek === 'peek' && !declinesDemo;
  const openByVerdict = peek === 'off' && access === 'enabled';
  const [answeredDemo, setAnsweredDemo] = useState(false);
  const lockedDemo = askedDemo || (openByVerdict && answeredDemo);
  const sectionAccess: VoiceSectionAccess =
    peek === 'waiting' ? 'loading' : askedDemo ? 'enabled' : access;
  return (
    <>
      {belowPlan && <VoiceDemoStatusProbe search={props.search} onStatus={setDemo} />}
      {openByVerdict && (
        <VoiceDemoAnswerProbe
          search={props.search}
          onSearchChange={props.onSearchChange}
          onAnswered={setAnsweredDemo}
          onDeclines={setOpenDeclinesDemo}
        />
      )}
      <LockedDemoProvider
        flag={VOICE_OF_CUSTOMER_FLAG_KEY}
        active={lockedDemo}
        asked={askedDemo}
        declined={declinesDemo}
      >
        <VoiceOfCustomerSection
          {...props}
          access={sectionAccess}
          onRetryAccess={retryAccess}
          lockedDemo={lockedDemo}
          sessionShell={sessionShell}
        />
      </LockedDemoProvider>
    </>
  );
}
